/**
 * gameStore — the React-side wrapper around the pure-functional core.
 *
 * The store holds GameState and exposes a `dispatch(action)` that runs the
 * pure reducer and replaces state. UI components subscribe to the store via
 * Zustand selectors. There is NO game logic here — all logic lives in
 * @aevons-wake/core. This file is glue.
 *
 * # Phase B addition
 *
 * The dispatch wrapper now intercepts `SAVE_LOAD_SUCCEEDED` actions to
 * replace state from a saved envelope. All other actions (combat, save
 * lifecycle informational, telemetry) flow through `reduce()` unchanged
 * — the combat reducer handles combat, save informational/telemetry are
 * no-ops at the reducer level (Phase A documented this).
 *
 * # Why state-replacement is in dispatch, not in the reducer
 *
 * Per Phase A's reducer.ts comment: "save lifecycle and telemetry actions
 * are handled by their respective reducers, composed via the gameStore."
 * Adding state-replacement to the combat reducer would expand its scope.
 * The gameStore is the natural composition point — it already wraps
 * `reduce()` and decides what state goes into the store.
 *
 * # Why no Zod in the SAVE_LOAD_SUCCEEDED handler
 *
 * Phase A's `deserialize()` already validated the envelope upstream when
 * the controller loaded it from disk. By the time `SAVE_LOAD_SUCCEEDED`
 * reaches the gameStore, the JSON in the action is guaranteed to have
 * passed full Zod validation in `deserialize()`. The hybrid guard below
 * is a structural sanity check (defends against future code paths that
 * might dispatch malformed JSON), not a re-validation.
 *
 * # Post combat-genre-pivot (v2 GameState)
 *
 * GameState fields are now `readonly` at the type level. `buildInitialState`
 * uses spread to construct the initial state with the seeded rng instead
 * of mutating the placeholder rng returned by `createInitialState`. This
 * preserves immutability discipline at the type level while giving us
 * a properly-seeded rng on first boot.
 */

import { create } from 'zustand';
import {
  reduce,
  createInitialState,
  SeededRng,
  type Action,
  type CombatState,
  type GameState,
} from '@aevons-wake/core';

interface GameStore {
  state: GameState;
  dispatch: (action: Action) => void;
  resetWithSeed: (seed: number) => void;
}

/**
 * Dev-only escape hatch action shape. NOT part of the canonical
 * `@aevons-wake/core` `Action` union — kept out of the union so the typed
 * reducer doesn't grow a dev-only branch. The dispatcher below intercepts
 * by string-tag at runtime, gated behind `import.meta.env.DEV`. Production
 * builds tree-shake the entire branch.
 *
 * Callers dispatch via `useGameStore.getState().dispatch(action as never)`
 * — the `as never` cast is the explicit declaration that they are entering
 * the dev escape hatch. See `App.tsx`'s `?dev=combat` URL-flag handler.
 */
interface DevFixtureCombatStateAction {
  readonly type: '__DEV_FIXTURE_COMBAT_STATE';
  readonly combatState: CombatState;
}

/**
 * Construct the initial GameState with a properly seeded rng snapshot.
 *
 * `createInitialState` returns a v2 GameState with a placeholder rng
 * (`{ s0: '0', s1: '0' }`). This wrapper replaces the placeholder with a
 * real seeded rng snapshot via spread (rather than mutation, since v2
 * GameState fields are `readonly` at the type level).
 */
function buildInitialState(seed: number): GameState {
  const base = createInitialState(seed);
  return {
    ...base,
    rng: new SeededRng(seed).serialize(),
  };
}

/**
 * Extract `gameState` from a SAVE_LOAD_SUCCEEDED action's envelopeJson.
 *
 * Hybrid validation strategy:
 *   - JSON.parse for speed (no Zod re-run)
 *   - Structural property checks to catch malformed payloads
 *   - Throws loudly on any structural mismatch — this surfaces bugs in
 *     the dispatcher rather than silently loading garbage state
 *
 * Future code paths that dispatch SAVE_LOAD_SUCCEEDED (Stage 1+ server
 * sync, save import from file, etc.) get the same backstop here for free.
 */
function extractGameState(envelopeJson: string): GameState {
  const parsed = JSON.parse(envelopeJson) as unknown;

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('state' in parsed) ||
    typeof parsed.state !== 'object' ||
    parsed.state === null ||
    !('gameState' in parsed.state) ||
    typeof parsed.state.gameState !== 'object' ||
    parsed.state.gameState === null ||
    !('version' in parsed.state.gameState) ||
    typeof parsed.state.gameState.version !== 'number'
  ) {
    throw new Error(
      'SAVE_LOAD_SUCCEEDED dispatched with malformed envelopeJson — ' +
        'expected envelope.state.gameState to exist with a numeric version',
    );
  }

  // Structural guard above narrowed enough to safely extract.
  // Cast is safe because Phase A's deserialize() ran upstream.
  return (parsed as { state: { gameState: GameState } }).state.gameState;
}

export const useGameStore = create<GameStore>((set) => ({
  state: buildInitialState(Date.now()),
  dispatch: (action) =>
    set((store) => {
      // Intercept SAVE_LOAD_SUCCEEDED to replace state from envelope.
      if (action.type === 'SAVE_LOAD_SUCCEEDED') {
        return { state: extractGameState(action.envelopeJson) };
      }

      // DEV-ONLY: intercept the dev fixture escape hatch. Vite replaces
      // `import.meta.env.DEV` with literal `false` in production builds,
      // tree-shaking this entire branch out of the production bundle.
      //
      // The runtime tag comparison sits behind a deliberate runtime cast
      // — the dev action is not in the typed `Action` union, so we cast
      // to a structural shape (`{ type: string }`) just to inspect the tag.
      // On match, narrow via `DevFixtureCombatStateAction` and replace the
      // `combat` slot on the existing GameState (no other field changes).
      if (
        import.meta.env.DEV &&
        (action as { type: string }).type === '__DEV_FIXTURE_COMBAT_STATE'
      ) {
        const devAction = action as unknown as DevFixtureCombatStateAction;
        return {
          state: {
            ...store.state,
            combat: devAction.combatState,
          },
        };
      }

      // DEV-ONLY: dev-fixture exit hatch — clears the combat slot back to
      // null so App.tsx's combat-active branch falls back to the main menu.
      // Used by CombatScreen's Esc-to-exit + corner exit button when the
      // dev fixture was injected via the ?dev=combat URL flag. Production
      // tree-shakes via import.meta.env.DEV literal-false.
      if (
        import.meta.env.DEV &&
        (action as { type: string }).type === '__DEV_CLEAR_COMBAT'
      ) {
        return {
          state: {
            ...store.state,
            combat: null,
          },
        };
      }

      return { state: reduce(store.state, action) };
    }),
  resetWithSeed: (seed) => set({ state: buildInitialState(seed) }),
}));
